import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import { CurrencyRef } from "../../../../models/WorkshopDTO";
import { ClassroomOptionDTO, MentorOptionDTO } from "../../../../models/StudentProgressDTO";
import { montoBonito } from "../../../../utils/monto";
import { fechaBonita } from "../../../../utils/fechaBonita";
import { cursoCorto } from "../../../../utils/cursoCorto";
//Talleres y Eventos comparten piezas y estilos con Actualizaciones (tokens --wa-*).
import "../enrollmentUpdates/styles.css";
import "./styles.css";
export { errorText } from "../enrollmentRequests/helpers";
export { PromptModal } from "../enrollmentUpdates/piezas";

/** Plantel de la barra (mismo criterio que Matrículas y Actualizaciones). */
export const usePlantel = (): { campusId: number | null; campusName: string; currency: CurrencyRef | null } => {
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	const cur: any = legacyCampus?.company?.currency || null;
	return { campusId: legacyCampus?.id ?? sessionCampus?.id ?? null, campusName: legacyCampus?.name || "",
		currency: cur && cur.code ? { id: cur.id, code: cur.code, symbol: cur.symbol } : null };
};

export const dinero = (cur: CurrencyRef | null | undefined, v: any): string => `${cur?.symbol || ""} ${montoBonito(v)}`.trim();
/** Código de la moneda para etiquetas: "Paquete graduada (LPS)". */
export const conMoneda = (label: string, cur: CurrencyRef | null | undefined): string => (cur?.code ? `${label} (${cur.code})` : label);
/** Monto con código de moneda: "L 1,500.00 LPS" (R2: Esteban quiere ver la moneda, no solo el símbolo). */
export const dineroCod = (cur: CurrencyRef | null | undefined, v: any): string => `${dinero(cur, v)}${cur?.code ? " " + cur.code : ""}`;

/**
 * Roles que pueden cobrar un precio distinto al del evento / taller o marcar pagado a mano (EventService::MONEY_ROLE_IDS):
 * Caja, Supervisión de Caja, Contraloría, Dirección General Torreón; superAdmin (0) en todos los planteles.
 */
export const MONEY_ROLE_IDS = [9, 10, 11, 19, 20, 30, 41, 43];
export const useRolDinero = (campusId: number | null): boolean => {
	const usuario: any = useSelector((state: RootState) => state.userProfile.value);
	const rol: any = usuario?.roleMap?.[usuario?.currentRole];
	if (!rol) return false;
	if (rol.id === 0) return true;
	return MONEY_ROLE_IDS.includes(Number(rol.id)) && (rol.campuses || []).some((c: any) => c.id === campusId);
};

/* "14:30" -> "2:30 pm" */
export const horaBonita = (hhmm: string | null | undefined): string => {
	const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ""));
	if (!m) return "";
	const h = Number(m[1]);
	return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? "am" : "pm"}`;
};

/** "2026-10-12 15:00:00" -> "Lun 12 oct 2026 3:00 pm" (sin pasar por UTC). */
export const fechaHora = (dt: string | null | undefined): string => {
	if (!dt) return "";
	const hora = String(dt).length > 10 ? horaBonita(String(dt).substring(11, 16)) : "";
	return fechaBonita(dt) + (hora && !String(dt).substring(11, 16).startsWith("00:00") ? " " + hora : "");
};

/** ISO UTC ("2026-10-02T02:26:00+00:00") a hora local del navegador. */
export const horaLocal = (iso: string | null | undefined): string => {
	if (!iso) return "";
	const d = new Date(iso);
	// am/pm, never 24 h (Módulos Claros, 2026-11-01)
	return isNaN(d.getTime()) ? iso : d.toLocaleString("es", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true }).replace(/\s*a\.?\s*m\.?/i, " am").replace(/\s*p\.?\s*m\.?/i, " pm");
};

/** "2026-10-12 15:00:00" <-> valor de <input type="datetime-local">. */
export const aInput = (dt: string | null | undefined): string => (dt ? String(dt).substring(0, 16).replace(" ", "T") : "");
export const deInput = (v: string): string | null => (v ? v.replace("T", " ") + (v.length === 16 ? ":00" : "") : null);

export const normalizar = (t: any): string => String(t || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

export const numOrNull = (v: string): number | null => (v.trim() === "" || isNaN(Number(v)) ? null : Number(v));

/** Los PDF y fotos llegan como Blob; un 422 también, con el mensaje adentro. */
export const blobErrorText = async (e: any): Promise<string> => {
	const data = e?.response?.data;
	if (data instanceof Blob) {
		try { const j = JSON.parse(await data.text()); return j.message || "No se pudo descargar."; } catch { return "No se pudo descargar."; }
	}
	return e?.response?.data?.message || "No se pudo descargar.";
};

export const guardarBlob = (blob: Blob, nombre: string) => {
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url; a.download = nombre; document.body.appendChild(a); a.click(); a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 4000);
};

export interface Opciones { cursos: { id: number; name: string }[]; aulas: ClassroomOptionDTO[]; maestras: MentorOptionDTO[]; }

/** Cursos (de los horarios activos), aulas (GET /classrooms?all=1) y maestras del plantel. */
export const useOpciones = (campusId: number | null): Opciones => {
	const [op, setOp] = useState<Opciones>({ cursos: [], aulas: [], maestras: [] });
	useEffect(() => {
		if (campusId === null) return;
		const http = HttpManager.getInstance();
		let vigente = true;
		http.scheduleService.getSchedulesByCampusId(campusId, 1000, { statusId: "1" })
			.then((r: any) => {
				const m = new Map<number, string>();
				// Nombre corto ("Barbería", no "Diplomado de Barbería"): la lista de chips se lee de un vistazo (Esteban, 2026-10-24)
				(r.data?.data || []).forEach((s: any) => { if (s.courseId && s.course) m.set(s.courseId, cursoCorto(s.course) || s.course); });
				if (vigente) setOp(o => ({ ...o, cursos: Array.from(m.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "es")) }));
			}).catch(() => { /* sin horarios */ });
		http.studentProgressService.getClassrooms(campusId)
			.then(r => vigente && setOp(o => ({ ...o, aulas: (Array.isArray(r.data) ? r.data : []).filter(a => a.statusId === 1) })))
			.catch(() => { /* sin permiso de aulas */ });
		http.studentProgressService.getMentors(campusId)
			.then(r => vigente && setOp(o => ({ ...o, maestras: Array.isArray(r.data) ? r.data.filter(m => m.enabled) : [] })))
			.catch(() => { /* sin maestras */ });
		return () => { vigente = false; };
	}, [campusId]);
	return op;
};

/** Paginador corto. */
export const Paginas: React.FC<{ page: number; last: number; total: number; onPage: (p: number) => void }> = ({ page, last, total, onPage }) =>
	last <= 1 ? null : (
		<div className="wau-acciones">
			<span className="wau-sec wau-num">Página {page} de {last} ({total})</span>
			<button type="button" className="wau-btn s mini" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
			<button type="button" className="wau-btn s mini" disabled={page >= last} onClick={() => onPage(page + 1)}>›</button>
		</div>
	);

/** Cerrar con una X en la esquina de arriba a la derecha (Esteban, 2026-10-23: en vez del botón Volver). */
export const BotonX: React.FC<{ onClick: () => void; disabled?: boolean }> = ({ onClick, disabled }) => (
	<button type="button" className="wte-x" aria-label="Cerrar" title="Cerrar" onClick={onClick} disabled={disabled}>×</button>
);

/** Ventana modal con el estilo de Actualizaciones. */
export const Modal: React.FC<{ title: string; onClose: () => void; ancho?: number; children: React.ReactNode }> = ({ title, onClose, ancho, children }) => (
	<div className="wau-modal-fondo" role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
		<div className="wau-modal" style={ancho ? { maxWidth: ancho } : undefined} onClick={e => e.stopPropagation()}>
			<div className="wte-modal-cab"><h3>{title}</h3><BotonX onClick={onClose} /></div>
			{children}
		</div>
	</div>
);

/** Estado como pastilla: programado naranja, realizado neutro (nunca verde), anulado oro, histórico suave. */
export const EstadoPill: React.FC<{ status: string; label: string }> = ({ status, label }) => (
	<span className={"wte-estado " + status}>{label}</span>
);

/** Busca alumnas del plantel (students/_list) y devuelve la elegida. */
export const BuscarAlumna: React.FC<{ campusId: number; onPick: (s: { id: number; name: string; code: string }) => void; disabled?: boolean; placeholder: string; boton: string }> =
	({ campusId, onPick, disabled, placeholder, boton }) => {
		const http = HttpManager.getInstance();
		const [q, setQ] = useState<string>("");
		const [found, setFound] = useState<{ id: number; name: string; code: string }[] | null>(null);
		const buscar = () => {
			if (q.trim().length < 2) return;
			http.studentService.getStudentsByCampusId(campusId, 10, { search: q.trim() })
				.then((r: any) => setFound((r.data?.data || []).map((s: any) => ({ id: s.id, name: `${s.firstName || ""} ${s.lastName || ""}`.trim(), code: s.identityCode || s.correlative || "" }))))
				.catch(() => setFound([]));
		};
		return (
			<div>
				<div className="wau-fila">
					<input type="search" className="wau-entrada" style={{ maxWidth: 320 }} placeholder={placeholder} value={q} disabled={disabled}
						onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === "Enter") buscar(); }} />
					<button type="button" className="wau-btn s" disabled={disabled || q.trim().length < 2} onClick={buscar}>{boton}</button>
				</div>
				{found === null ? null : (
					<div className="wau-lista" style={{ marginTop: 8, maxWidth: 520 }}>
						{found.length === 0 ? <span className="wau-sec">Sin resultados.</span> : found.map(s => (
							<button key={s.id} type="button" className="wau-item" onClick={() => { onPick(s); setFound(null); setQ(""); }}>
								<span>{s.name}</span><span className="sub">{s.code}</span>
							</button>
						))}
					</div>
				)}
			</div>
		);
	};
